import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { stopPortalEvents } from './portalGuard';

export interface ArchiveSubject {
  patientName: string;
  /** Already formatted for reading, e.g. "12 Aug". */
  serviceDate: string;
  payerName: string;
}

export interface ArchiveDialogProps {
  open: boolean;
  subject: ArchiveSubject;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (opts: { note?: string }) => void;
}

/**
 * Archive, with the sentence that makes it safe to press (GDN-3380).
 *
 * A dialog rather than a menu item that just fires, because archive is the
 * one verb here with no visible end date: the biller is told, before they
 * commit, both how wide it reaches (every claim for the encounter, not the
 * claim they happen to be looking at) and that it is not permanent. The hide
 * rule already guarantees the second half; saying so is what stops billers
 * from avoiding the verb and letting the queue grow instead.
 *
 * The note is optional and is the only place the reason gets recorded. Its
 * helper says who can see it, because a biller writing "patient is a friend
 * of the owner, do not chase" deserves to know their colleagues will read it.
 */
export function ArchiveDialog({ open, subject, busy, onCancel, onConfirm }: ArchiveDialogProps) {
  const [note, setNote] = useState('');

  /* A reopened dialog is a new decision, not a resumed one. */
  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  const confirm = useCallback(() => {
    onConfirm({ note: note.trim() || undefined });
  }, [note, onConfirm]);

  return (
    <Dialog {...stopPortalEvents} fullWidth maxWidth="xs" open={open} onClose={onCancel}>
      <DialogTitle sx={{ fontSize: '1.0625rem', fontWeight: 600, pb: 1 }}>Archive</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', mb: 2 }}>
          Hides{' '}
          <Typography
            component="span"
            sx={{ color: 'text.primary', fontWeight: 600, fontSize: 'inherit' }}
          >
            {subject.patientName} · {subject.serviceDate} · {subject.payerName}
          </Typography>{' '}
          and every claim for that encounter from all queues. It comes back on its own if the payer
          responds or a corrected claim is created.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Note (optional)"
          value={note}
          onChange={event => setNote(event.target.value)}
          /* Enter commits, Shift+Enter breaks the line. The note is one or two
             sentences in practice, and a biller who has typed the reason wants
             the same key that ends every other short form in the app. */
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (!busy) confirm();
            }
          }}
          helperText="Shown to everyone at the clinic, with your name and the time."
          slotProps={{ htmlInput: { 'aria-label': 'Note (optional)' } }}
        />
      </DialogContent>
      {/* Sentence case, not MUI's default SHOUTING: the mockup reads "Cancel"
          and "Archive", and an uppercased label is a different word on the
          screen from the one the copy deck pins. */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onCancel} size="small" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          disabled={busy}
          onClick={confirm}
          size="small"
          sx={{ textTransform: 'none' }}
          variant="contained"
        >
          Archive
        </Button>
      </DialogActions>
    </Dialog>
  );
}
